import { useForm, useFieldArray, Resolver, FieldValue, FieldValues } from 'react-hook-form';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
  InputGroup,
} from '@chakra-ui/react';
import { modalFormSchema, type ModalFormData } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { Polls, PollsInteractivity, PollsStatus } from '@/model/polls';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePutPollsMutation } from '@/services/queries/polls';
import { useEffect, useRef, useMemo } from 'react';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { ImagePreview } from '@/components/ImagePreview';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { compressImageToBudget, renameFile } from '@/utils/image';
import { MAX_POLL_OPTIONS } from '../../constants';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface PollsEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: Polls;
  onClose: () => void;
  onDone?: () => void;
}

export const PollsEditModal = ({ isOpen, onClose, onDone, item }: PollsEditModalProps) => {
  const toast = useToast();
  const { t } = useTranslation();

  const putMutation = usePutPollsMutation();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resolver: Resolver<ModalFormData> = zodResolver(modalFormSchema);

  const form = useForm<ModalFormData>({
    resolver,
    defaultValues: {
      team_uuid: '',
      question: '',
      identifier: '',
      status: PollsStatus.NOT_PUBLISHED,
      allow_multiple_votes: false,
      show_partial_results: false,
      disable_comments: false,
      enable_home: true,
      has_closing_date: false,
      closing_date: '',
      poll_options: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: 'poll_options',
    control: form.control,
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
    control,
  } = form;
  const formValues = watch();

  const isLocked = item?.interactivity === PollsInteractivity.CLOSED;

  const imagePreviewUrls = useMemo(() => {
    return fields.map(field => {
      if (field.image) {
        return field.image instanceof File ? URL.createObjectURL(field.image) : field.image;
      }
      return undefined;
    });
  }, [fields]);

  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviewUrls]);

  const handlePreviewActionClick = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (currentField) {
      update(index, {
        ...currentField,
        image: undefined,
      });
    }

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handlePreviewDeleteClick = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (inputRefs?.current?.[index]) {
      const input = inputRefs.current[index] as HTMLInputElement;
      input.value = '';
      input.files = null;
    }

    if (currentField) {
      update(index, {
        ...currentField,
        image: undefined,
      });
    }
  };

  const handleSavePollQuestion = async (index: number) => {
    await form.trigger(`poll_options.${index}.option_text`);
    await form.trigger(`poll_options.${index}.image`);

    if (getPollOptionFieldInvalid(index)) return;

    const currentField = formValues?.poll_options?.[index];

    update(index, {
      ...currentField,
      is_editing: false,
    });
  };

  const handleEditPollQuestion = (index: number) => {
    const currentField = formValues?.poll_options?.[index];

    if (currentField) {
      update(index, {
        ...currentField,
        is_editing: true,
      });
    }
  };

  const handleDeletePollQuestion = (index: number) => {
    remove(index);
  };

  const handleEdit = (data: ModalFormData) => {
    const mediaFiles: Record<string, File> = {};

    data.poll_options?.forEach(option => {
      if (option.image) {
        mediaFiles[option.uuid] = option.image;
      }
    });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      question: data.question,
      identifier: data.identifier,
      allow_multiple_votes: data.allow_multiple_votes,
      show_partial_results: data.show_partial_results,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      status: data.status as PollsStatus,
      closing_date: data.has_closing_date ? data.closing_date : null,
      poll_options:
        data.poll_options?.map(option => ({
          uuid: option.uuid,
          option_text: option.option_text,
        })) ?? [],
      ...mediaFiles, // aqui já espalha os arquivos com nome = uuid
    };

    putMutation.mutate(
      { id: item.uuid, payload },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_polls.components.polls_edit_modal.polls_edit_update_success'),
            status: 'success',
          });

          onDone?.();
          reset();
        },
        onError: (e: any) => {
          toast({
            title: e?.message || t('pages.application_polls.components.polls_edit_modal.polls_edit_update_error'),
            status: 'error',
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const getPollOptionFieldInvalid = (index: number) => {
    const errorField = form?.formState?.errors?.poll_options?.[index];

    return !!errorField?.option_text || !!errorField?.image;
  };

  const getPollOptionFieldErrorMessage = (index: number) => {
    const errorField = form?.formState?.errors?.poll_options?.[index];

    return errorField?.option_text?.message?.toString() ?? errorField?.image?.message?.toString() ?? '';
  };

  useEffect(() => {
    if (!item) return;

    setValue('team_uuid', item.team_uuid);
    setValue('question', item.question);
    setValue('identifier', item.identifier);
    setValue('status', item.status);
    setValue('allow_multiple_votes', item.allow_multiple_votes);
    setValue('show_partial_results', item.show_partial_results);
    setValue('disable_comments', item.disable_comments);
    setValue('enable_home', item.enable_home);
    setValue('has_closing_date', !!item.closing_date);
    setValue(
      'closing_date',
      item.closing_date ? formatDateToDateTimeLocalInputValue(item.closing_date) : '',
    );

    if (item?.poll_options?.length) {
      item?.poll_options?.map(async (poll_option, index) => {
        const updatedPollOption: FieldValue<FieldValues> = {
          uuid: poll_option.uuid,
          option_text: poll_option.option_text,
          image: poll_option.image_url,
          is_editing: false,
        };

        setValue(`poll_options.${index}`, updatedPollOption);
        update(index, updatedPollOption);
      });
    }
  }, [setValue, item, update]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' onSubmit={handleSubmit(handleEdit)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_polls.components.polls_edit_modal.polls_edit_title')}
            <Flex gap={2} alignItems='center'>
              <Text fontSize='14px' fontWeight='normal'>
                {t('pages.application_polls.components.polls_edit_modal.polls_published_label')}
              </Text>
              <Switch
                isChecked={formValues.status === PollsStatus.PUBLISHED}
                onChange={event =>
                  form.setValue('status', event.target.checked ? PollsStatus.PUBLISHED : PollsStatus.NOT_PUBLISHED)
                }
                display='flex'
                alignItems='center'
                size='sm'
              />
            </Flex>
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_polls.components.polls_edit_modal.polls_edit_team_label')}
                placeholder={t('pages.application_polls.components.polls_edit_modal.polls_edit_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />

              <InputField
                isDisabled={isLocked}
                withoutFloating
                label={t('pages.application_polls.components.polls_edit_modal.polls_edit_label')}
                helperText={t('pages.application_polls.components.polls_edit_modal.polls_edit_helper_text_20')}
                disabled={item.status !== PollsStatus.DRAFT}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                isDisabled={isLocked}
                withoutFloating
                label={t('pages.application_polls.components.polls_edit_modal.polls_edit_label_enq')}
                helperText={t('pages.application_polls.components.polls_edit_modal.polls_edit_helper_text_100')}
                disabled={item.status !== PollsStatus.DRAFT}
                maxLength={100}
                {...register('question')}
                rootProps={{ isInvalid: !!errors?.question, errorMessage: errors?.question?.message }}
              />

              <InputGroup display='flex' flexDirection='column' gap={3}>
                <Text fontWeight='medium'>
                  {t('pages.application_polls.components.polls_edit_modal.polls_edit_title_response')}
                </Text>

                {fields.map((field, index) => (
                  <Flex key={field.id} gap={3} alignItems='center'>
                    {field.is_editing && !field.image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          disabled={isLocked || item.status !== PollsStatus.DRAFT}
                          ref={el => (inputRefs.current[index] = el)}
                          label={t('pages.application_polls.components.polls_edit_modal.polls_edit_image_label')}
                          onFileChange={async file => {
                            if (!file) return;

                            const currentField = formValues?.poll_options?.[index];

                            if (currentField) {
                              const compressedFile = await compressImageToBudget(file);
                              const renamedFile = renameFile(compressedFile, field.uuid);
                              const updatedField = {
                                ...currentField,
                                image: renamedFile,
                              };

                              form.setValue(`poll_options.${index}`, updatedField);
                              update(index, updatedField);
                            }

                            form.trigger(`poll_options.${index}.image`);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={imagePreviewUrls[index]}
                        actionIconName='image'
                        onActionClick={() => handlePreviewActionClick(index)}
                        onDeleteClick={() => handlePreviewDeleteClick(index)}
                        withoutHover={!field.is_editing}
                      />
                    )}

                    {field?.is_editing ? (
                      <InputField
                        disabled={isLocked || item.status !== PollsStatus.DRAFT}
                        isDisabled={isLocked || item.status !== PollsStatus.DRAFT}
                        withoutFloating
                        label={t('pages.application_polls.components.polls_edit_modal.polls_edit_option_label', {
                          index: index + 1,
                        })}
                        helperText={t('pages.application_polls.components.polls_edit_modal.polls_edit_helper_text_50')}
                        maxLength={50}
                        width='100%'
                        {...form.register(`poll_options.${index}.option_text`)}
                        rootProps={{
                          isInvalid: getPollOptionFieldInvalid(index),
                          errorMessage: getPollOptionFieldErrorMessage(index),
                        }}
                      />
                    ) : (
                      <Flex direction='column' gap={1} w='100%'>
                        <Text fontWeight='medium'>
                          {t('pages.application_polls.components.polls_edit_modal.polls_edit_option_label', {
                            index: index + 1,
                          })}
                        </Text>
                        <Text>{field.option_text}</Text>
                      </Flex>
                    )}

                    {!isLocked && item.status === PollsStatus.DRAFT && (
                      <Flex gap={4} alignItems='center'>
                        {field?.is_editing ? (
                          <>
                            <Button size='xs' h='24px' onClick={() => handleSavePollQuestion(index)}>
                              {t('pages.application_polls.components.polls_edit_modal.polls_edit_save_option')}
                            </Button>
                            <ChakraFeatherIcon
                              name='trash-2'
                              color='red.500'
                              w='20px'
                              cursor='pointer'
                              onClick={() => handleDeletePollQuestion(index)}
                            />
                          </>
                        ) : (
                          <>
                            <ChakraFeatherIcon
                              name='edit-2'
                              color='brand.500'
                              w='20px'
                              cursor='pointer'
                              onClick={() => handleEditPollQuestion(index)}
                            />
                            <ChakraFeatherIcon
                              name='trash-2'
                              color='red.500'
                              w='20px'
                              cursor='pointer'
                              onClick={() => handleDeletePollQuestion(index)}
                            />
                          </>
                        )}
                      </Flex>
                    )}
                  </Flex>
                ))}

                {form.formState.errors?.poll_options?.root ? (
                  <Text fontSize='14px' color='red.500'>
                    {form.formState.errors?.poll_options?.root?.message}
                  </Text>
                ) : null}

                <Button
                  disabled={
                    isLocked || item.status !== PollsStatus.DRAFT || fields.length >= MAX_POLL_OPTIONS
                  }
                  alignSelf='flex-start'
                  variant='link'
                  color='brand.500'
                  textDecor='underline'
                  display='flex'
                  alignItems='center'
                  gap={2}
                  onClick={() => {
                    form.clearErrors();
                    append({ uuid: crypto.randomUUID(), option_text: '', image: '', is_editing: true });
                  }}
                >
                  <ChakraFeatherIcon name='plus' />
                  {t('pages.application_polls.components.polls_edit_modal.polls_edit_add_option')}
                </Button>
              </InputGroup>

              <Switch
                isChecked={!!formValues.has_closing_date}
                onChange={event => setValue('has_closing_date', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_edit_modal.polls_edit_has_closing_date')}
              </Switch>

              {formValues?.has_closing_date ? (
                <InputField
                  withoutFloating
                  label={t('pages.application_polls.components.polls_edit_modal.polls_edit_label_date')}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(
                    item.closing_date && new Date(item.closing_date) < new Date() ? item.closing_date : new Date(),
                  )}
                  rootProps={{ isInvalid: !!errors?.closing_date, errorMessage: errors?.closing_date?.message }}
                  {...register('closing_date')}
                />
              ) : null}

              <Switch
                isChecked={!!formValues.allow_multiple_votes}
                onChange={event => setValue('allow_multiple_votes', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_edit_modal.polls_edit_allow_multiple_votes')}
              </Switch>

              <Switch
                isChecked={!!formValues.show_partial_results}
                onChange={event => setValue('show_partial_results', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_edit_modal.polls_edit_show_partial_results')}
              </Switch>

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_edit_modal.polls_edit_disable_comments')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_polls.components.polls_edit_modal.polls_edit_enable_home')}
              </Switch>              
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Button type='button' variant='outlined' color='gray.600' onClick={handleClose}>
              {t('pages.application_polls.components.polls_edit_modal.polls_edit_cancel')}
            </Button>
            <Button type='submit'>{t('pages.application_polls.components.polls_edit_modal.polls_edit_save_changes')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={putMutation.isLoading} />
    </>
  );
};
